import { useNavigate } from "react-router-dom";
import { BadgeCheck, Crown } from "lucide-react";
import { useAjudaPapel } from "@/core/hooks/useAjudaPapel";
import { useProgressoAjuda } from "@/core/hooks/useProgressoAjuda";
import { licaoNovaParaInicio } from "@/core/ajuda/licoes";

const CARTOES = {
  liderar: {
    titulo: "Novo para quem lidera",
    texto: "Veja como criar funções, convidar pessoas e quem cuida de cada área.",
    icone: Crown,
    classeIcone: "bg-warning/10 text-warning",
  },
  gestor: {
    titulo: "Novo para quem gerencia um elo",
    texto: "Veja o que você faz em cada elo e onde aparece o seu selo.",
    icone: BadgeCheck,
    classeIcone: "bg-info/10 text-info dark:bg-info/30 dark:text-text",
  },
} as const;

/**
 * Cartão do Início para quem lidera um ministério ou é Gestor de elo: convida para a lição do papel
 * dela ("Liderar um ministério" ou "Gestor de elo"). Aprovado em 02/11/2026 (protótipo "Ajuda:
 * ministérios, funções e gestão", v2). Segue o jeito do cartão "Novo por aqui?": só convida, fecha
 * com "Agora não" e some sozinho quando a lição é vista. Mostra um por vez (primeiro o de quem
 * lidera). A lição continua na Ajuda, no menu da conta.
 */
export function ConviteAjudaPapel() {
  const navigate = useNavigate();
  const { papel, pronto: papelPronto } = useAjudaPapel();
  const { progresso, pronto, dispensarCartaoLicao } = useProgressoAjuda();

  if (!pronto || !papelPronto) return null;
  const licaoId = licaoNovaParaInicio(papel, progresso.vistas, progresso.cartoesDispensados);
  if (!licaoId) return null;

  const { titulo, texto, icone: Icone, classeIcone } = CARTOES[licaoId];

  return (
    <section
      aria-label={titulo}
      className="mb-6 flex flex-col gap-4 rounded-2xl border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:p-5"
    >
      <span
        aria-hidden="true"
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${classeIcone}`}
      >
        <Icone size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold text-text">{titulo}</p>
        <p className="text-sm text-muted">{texto}</p>
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={() => navigate(`/ajuda/${licaoId}`)}
          className="min-h-11 rounded-lg bg-primary px-4 text-sm font-bold text-on-primary transition-colors hover:bg-primary-hover"
        >
          Abrir a lição
        </button>
        <button
          type="button"
          onClick={() => dispensarCartaoLicao(licaoId)}
          className="min-h-11 rounded-lg border border-border bg-surface px-4 text-sm font-bold text-muted transition-colors hover:bg-surface-2 hover:text-text"
        >
          Agora não
        </button>
      </div>
    </section>
  );
}
